import React, { useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { AuthContext, API } from '../shared';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Progress } from './ui/progress';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Database, Download, Upload, ShieldAlert, CheckCircle2, RefreshCw } from 'lucide-react';

/**
 * 🆕 Full-application Backup & Restore UI.
 *
 * The backup module on the backend is self-contained — this page is just
 * the admin-facing control panel. Three flows:
 *
 *   1. Create backup      → POST /api/full-backup/create        (returns job_id)
 *   2. Poll progress      → GET  /api/full-backup/jobs/{id}     (updates bar)
 *   3. Download .sanhadja → GET  /api/full-backup/download/{id}
 *
 *   4. Restore upload     → POST /api/full-backup/restore (multipart)
 *   5. Poll restore state → GET  /api/full-backup/restore-jobs/{id}
 */
export default function BackupRestore() {
  const { user } = useContext(AuthContext);
  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
  const isSuperAdmin = user?.role === 'super_admin';

  const [activeJob, setActiveJob] = useState(null);
  const [restoreJob, setRestoreJob] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [restoreFile, setRestoreFile] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const pollRef = useRef(null);
  const restorePollRef = useRef(null);

  const fetchJobs = async () => {
    try {
      const r = await axios.get(`${API}/full-backup/jobs`, auth());
      setJobs(Array.isArray(r.data) ? r.data : []);
    } catch (_) {
      setJobs([]);
    }
  };

  useEffect(() => {
    if (isSuperAdmin) fetchJobs();
  }, [isSuperAdmin]);

  // Poll an active backup job every 1.5s until it's done/failed.
  useEffect(() => {
    if (!activeJob?.id || activeJob.status === 'done' || activeJob.status === 'failed') {
      clearInterval(pollRef.current);
      return;
    }
    pollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/full-backup/jobs/${activeJob.id}`, auth());
        setActiveJob(r.data);
        if (r.data.status === 'done' || r.data.status === 'failed') {
          fetchJobs();
        }
      } catch (_) { /* noop */ }
    }, 1500);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [activeJob?.id, activeJob?.status]);

  // Same for the restore job.
  useEffect(() => {
    if (!restoreJob?.id || restoreJob.status === 'done' || restoreJob.status === 'failed') {
      clearInterval(restorePollRef.current);
      return;
    }
    restorePollRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/full-backup/restore-jobs/${restoreJob.id}`, auth());
        setRestoreJob(r.data);
      } catch (_) { /* noop */ }
    }, 1500);
    return () => clearInterval(restorePollRef.current);
    // eslint-disable-next-line
  }, [restoreJob?.id, restoreJob?.status]);

  const startBackup = async () => {
    try {
      const r = await axios.post(`${API}/full-backup/create`, {}, auth());
      setActiveJob({ id: r.data.job_id, status: 'queued', progress: 0, step: 'في الطابور…' });
    } catch (e) {
      alert('❌ فشل بدء النسخ: ' + (e.response?.data?.detail || e.message));
    }
  };

  const downloadBackup = (jobId) => {
    // Open via blob so the Authorization header is sent — a direct
    // window.open would lose it.
    axios
      .get(`${API}/full-backup/download/${jobId}`, { ...auth(), responseType: 'blob' })
      .then((resp) => {
        const url = window.URL.createObjectURL(new Blob([resp.data]));
        const link = document.createElement('a');
        const cd = resp.headers['content-disposition'] || '';
        const m = cd.match(/filename="?([^"]+)"?/i);
        link.href = url;
        link.download = m ? m[1] : `sanhaja_backup_${jobId.slice(0, 8)}.sanhadja`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      })
      .catch((e) => alert('❌ فشل التنزيل: ' + (e.response?.data?.detail || e.message)));
  };

  const submitRestore = async () => {
    if (!restoreFile) return;
    setConfirmRestore(false);
    try {
      const fd = new FormData();
      fd.append('file', restoreFile);
      const r = await axios.post(`${API}/full-backup/restore`, fd, auth());
      setRestoreJob({ id: r.data.job_id, status: 'queued', progress: 0, step: 'في الطابور…' });
    } catch (e) {
      alert('❌ فشل الاسترجاع: ' + (e.response?.data?.detail || e.message));
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="p-6" dir="rtl">
        <Card>
          <CardContent className="p-12 text-center">
            <ShieldAlert className="h-12 w-12 mx-auto text-rose-500 mb-4" />
            <h2 className="text-xl font-bold">⛔ النسخ الاحتياطي مُتاح للمدير العام فقط</h2>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" dir="rtl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-extrabold text-amber-900 flex items-center gap-2">
            <Database className="h-7 w-7" /> النسخ الاحتياطي والاسترجاع
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            وحدة مستقلَّة لإنشاء نسخة كاملة <span dir="ltr">(.sanhadja)</span> أو استرجاعها — لا تؤثر على نظام التقارير والتصدير الحالي.
          </p>
        </div>
        <Button variant="outline" onClick={fetchJobs} data-testid="refresh-jobs-btn">
          <RefreshCw className="h-4 w-4 ml-1" /> تحديث
        </Button>
      </div>

      <Tabs defaultValue="backup">
        <TabsList>
          <TabsTrigger value="backup" data-testid="tab-backup">💾 إنشاء نسخة</TabsTrigger>
          <TabsTrigger value="restore" data-testid="tab-restore">♻️ استرجاع</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">📋 السجل</TabsTrigger>
        </TabsList>

        {/* === BACKUP === */}
        <TabsContent value="backup">
          <Card>
            <CardHeader>
              <CardTitle>إنشاء نسخة احتياطية كاملة</CardTitle>
              <CardDescription>
                يشمل: قاعدة البيانات، الإعدادات، صور الوكالات والعمليات، التذاكر والوثائق المرفوعة، GridFS.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <Button
                onClick={startBackup}
                disabled={activeJob && activeJob.status === 'running'}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="create-backup-btn"
              >
                <Database className="h-4 w-4 ml-1" />
                {activeJob && activeJob.status === 'running' ? '⏳ جاري الإنشاء…' : '💾 إنشاء نسخة احتياطية'}
              </Button>

              {activeJob && (
                <div className="border-2 border-amber-200 bg-amber-50/50 rounded-lg p-4 space-y-3" data-testid="active-backup-progress">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold">{activeJob.step || '—'}</span>
                    <span dir="ltr" className="font-mono">{Math.round(activeJob.progress || 0)}%</span>
                  </div>
                  <Progress value={activeJob.progress || 0} className="h-2" />
                  {activeJob.status === 'done' && (
                    <Alert className="bg-green-50 border-green-300">
                      <CheckCircle2 className="h-4 w-4 text-green-700" />
                      <AlertDescription className="text-green-900">
                        ✅ اكتملت النسخة • <b>{activeJob.size_human || ''}</b>
                        {activeJob.manifest && (
                          <span className="block text-xs text-green-800 mt-1">
                            {Object.keys(activeJob.manifest.collections || {}).length} مجموعة
                            • {activeJob.manifest.files_count || 0} ملف وسائط
                            • {activeJob.manifest.gridfs_count || 0} ملف GridFS
                          </span>
                        )}
                        <Button
                          onClick={() => downloadBackup(activeJob.id)}
                          className="mt-2 bg-green-600 hover:bg-green-700 text-white"
                          size="sm"
                          data-testid="download-backup-btn"
                        >
                          <Download className="h-4 w-4 ml-1" /> تنزيل ملف .sanhadja
                        </Button>
                      </AlertDescription>
                    </Alert>
                  )}
                  {activeJob.status === 'failed' && (
                    <Alert variant="destructive">
                      <AlertDescription>❌ فشل: {activeJob.error || 'سبب غير معروف'}</AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === RESTORE === */}
        <TabsContent value="restore">
          <Card>
            <CardHeader>
              <CardTitle className="text-rose-800">♻️ استرجاع من ملف .sanhadja</CardTitle>
              <CardDescription>
                <span className="text-rose-700 font-semibold">⚠️ تحذير:</span> الاسترجاع
                <b> يستبدل </b>
                البيانات الحالية بالكامل. يُحتفظ بنسخة احتياطية في الذاكرة للتراجع التلقائي عند الفشل.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>اختر ملف النسخة (<span dir="ltr">.sanhadja</span>)</Label>
                <Input
                  type="file"
                  accept=".sanhadja"
                  onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                  data-testid="restore-file-input"
                />
                {restoreFile && (
                  <div className="text-xs text-gray-700 mt-1">
                    📦 {restoreFile.name} ({(restoreFile.size / (1024 * 1024)).toFixed(1)} MB)
                  </div>
                )}
              </div>

              <Button
                onClick={() => setConfirmRestore(true)}
                disabled={!restoreFile || (restoreJob && restoreJob.status === 'running')}
                className="bg-rose-600 hover:bg-rose-700 text-white"
                data-testid="start-restore-btn"
              >
                <Upload className="h-4 w-4 ml-1" />
                {restoreJob && restoreJob.status === 'running' ? '⏳ جاري الاسترجاع…' : '♻️ بدء الاسترجاع'}
              </Button>

              {restoreJob && (
                <div className="border-2 border-rose-200 bg-rose-50/50 rounded-lg p-4 space-y-3" data-testid="restore-progress">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-semibold">{restoreJob.step || '—'}</span>
                    <span dir="ltr" className="font-mono">{Math.round(restoreJob.progress || 0)}%</span>
                  </div>
                  <Progress value={restoreJob.progress || 0} className="h-2" />
                  {restoreJob.status === 'done' && (
                    <Alert className="bg-green-50 border-green-300">
                      <AlertDescription className="text-green-900">
                        ✅ تم الاسترجاع بنجاح — يُنصح بإعادة تحميل التطبيق.
                      </AlertDescription>
                    </Alert>
                  )}
                  {restoreJob.status === 'failed' && (
                    <Alert variant="destructive">
                      <AlertDescription>❌ فشل: {restoreJob.error || '—'} (تم التراجع التلقائي)</AlertDescription>
                    </Alert>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* === HISTORY === */}
        <TabsContent value="history">
          <Card>
            <CardHeader>
              <CardTitle>سجل النسخ الأخيرة</CardTitle>
              <CardDescription>آخر 25 عملية (نسخ أو استرجاع) في هذه الجلسة.</CardDescription>
            </CardHeader>
            <CardContent>
              {jobs.length === 0 ? (
                <div className="text-center text-gray-500 py-6">لا توجد عمليات سابقة</div>
              ) : (
                <div className="space-y-2">
                  {jobs.map((j) => (
                    <div key={j.id} className="flex items-center justify-between border rounded-lg p-3 hover:bg-amber-50/40 transition-colors" data-testid={`job-row-${j.id}`}>
                      <div>
                        <div className="font-semibold text-sm">
                          {j.type === 'backup' ? '💾 نسخة' : '♻️ استرجاع'} ·{' '}
                          <Badge variant={j.status === 'done' ? 'default' : j.status === 'failed' ? 'destructive' : 'secondary'}>
                            {j.status}
                          </Badge>
                        </div>
                        <div className="text-xs text-gray-600 mt-1" dir="ltr">{j.created_at}</div>
                        <div className="text-xs text-gray-500">
                          {j.size_human ? `📦 ${j.size_human}` : ''} {j.step ? `· ${j.step}` : ''}
                        </div>
                      </div>
                      {j.type === 'backup' && j.status === 'done' && (
                        <Button size="sm" variant="outline" onClick={() => downloadBackup(j.id)} data-testid={`download-job-${j.id}`}>
                          <Download className="h-4 w-4 ml-1" /> تنزيل
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* === Restore confirmation === */}
      <Dialog open={confirmRestore} onOpenChange={setConfirmRestore}>
        <DialogContent data-testid="restore-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-800">⚠️ تأكيد الاسترجاع</DialogTitle>
            <DialogDescription className="pt-2 text-right leading-relaxed">
              ستُستبدل <b>كل البيانات الحالية</b> بمحتويات الملف:
              <br />
              <span className="font-mono text-xs" dir="ltr">{restoreFile?.name}</span>
              <br /><br />
              في حال فشل الاسترجاع، سيُحاول النظام التراجع تلقائياً من نسخة الذاكرة المؤقَّتة.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setConfirmRestore(false)} data-testid="restore-cancel-btn">إلغاء</Button>
            <Button onClick={submitRestore} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="restore-confirm-btn">
              ♻️ نعم، استرجع الآن
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
